import React, { createContext, useContext, useState, useEffect } from 'react';
import { format, differenceInDays } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { callQuickInsight } from '../utils/groq';

export interface JournalEntry {
  id: string;
  date: string;
  content: string;
  mood?: string;
  tags?: string[];
  isPinned?: boolean;
  isStarred?: boolean;
  isArchived?: boolean;
  updatedAt?: string;
}

export interface MindShift {
  id: string;
  date: string;
  content: string;
  isPinned?: boolean;
  isStarred?: boolean;
  tags?: string[];
  category?: 'belief' | 'emotional' | 'self-concept' | 'universal';
  relatedJournalId?: string;
}

export interface Action {
  id: string;
  content: string;
  description?: string;
  date: string;
  dueDate?: string;
  completed: boolean;
  isPinned?: boolean;
  isRecurring?: boolean;
  recurringFrequency?: 'daily' | 'weekly' | 'monthly';
  tags?: string[];
  fromMindshiftId?: string;
  completedDate?: string;
  reflection?: string;
  relatedEntryId?: string;
}

export interface Note {
  id: string;
  content: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DailyMoodEntry {
  id: string;
  date: string;
  mood: 'positive' | 'neutral' | 'challenging';
  energy: number;
  stress: number;
  gratitude: number;
  clarity: number;
  connection: number;
  notes?: string;
  createdAt: string;
}

export interface SophiaEmotionalTheme {
  emotion: string;
  prevalence: number;
  description: string;
  expression: 'positive' | 'negative' | 'neutral';
  color?: string;
}

interface JournalContextType {
  journalEntries: JournalEntry[];
  isLoading: boolean;
  generateActionSuggestion: (content: string) => Promise<string>;
  addActionFromMindshift: (mindshiftId: string, title: string) => Promise<void>;
  updateActionReflection: (id: string, reflection: string) => Promise<void>;
  getActionSuccessRate: () => number;
  getTodaysActions: () => Action[];
  getPinnedActions: () => Action[];
  getCompletedActions: () => Action[];
  addJournalEntry: (content: string, mood?: string, tags?: string[]) => Promise<void>;
  updateJournalEntry: (id: string, updates: Partial<JournalEntry>) => Promise<void>;
  deleteJournalEntry: (id: string) => Promise<void>;
  toggleEntryPinned: (id: string) => Promise<void>;
  toggleEntryStarred: (id: string) => Promise<void>;
  toggleEntryArchived: (id: string) => Promise<void>;
  searchJournalEntries: (query: string) => JournalEntry[];
  filterJournalEntries: (options: {
    tags?: string[];
    mood?: string;
    startDate?: string;
    endDate?: string;
    archived?: boolean;
  }) => JournalEntry[];
  mindshifts: MindShift[];
  addMindshift: (content: string) => Promise<void>;
  updateMindshift: (id: string, updates: Partial<MindShift>) => Promise<void>;
  deleteMindshift: (id: string) => Promise<void>;
  toggleMindshiftPinned: (id: string) => Promise<void>;
  toggleMindshiftStarred: (id: string) => Promise<void>;
  searchMindshifts: (query: string) => MindShift[];
  filterMindshifts: (options: {
    tags?: string[];
    category?: string;
    startDate?: string;
    endDate?: string;
    onlyStarred?: boolean;
    onlyPinned?: boolean;
  }) => MindShift[];
  actions: Action[];
  addAction: (
    content: string,
    description?: string,
    options?: {
      dueDate?: string;
      isRecurring?: boolean;
      recurringFrequency?: 'daily' | 'weekly' | 'monthly';
      tags?: string[];
      fromMindshiftId?: string;
      relatedEntryId?: string;
    }
  ) => Promise<void>;
  toggleActionCompleted: (id: string) => Promise<void>;
  toggleActionPinned: (id: string) => Promise<void>;
  deleteAction: (id: string) => Promise<void>;
  updateAction: (id: string, updates: Partial<Action>) => Promise<void>;
  notes: Note[];
  addNote: (content: string) => Promise<void>;
  updateNote: (id: string, content: string) => Promise<void>;
  toggleNotePinned: (id: string) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  getLoginStreak: () => number;
  getWeeklyEntries: () => JournalEntry[];
  getWeeklyMindshifts: () => MindShift[];
  getCurrentMood: () => string | undefined;
  setCurrentMood: (mood: string) => void;
  dailyMoods: DailyMoodEntry[];
  addDailyMood: (moodData: Omit<DailyMoodEntry, 'id' | 'date' | 'createdAt'>) => Promise<void>;
  getTodaysMoodEntry: () => DailyMoodEntry | undefined;
  hasMoodEntryForToday: () => boolean;
  getWeeklyMoodData: () => DailyMoodEntry[];
  getMoodTrends: () => {
    averageMood: number;
    averageEnergy: number;
    averageStress: number;
    averageGratitude: number;
    averageClarity: number;
    averageConnection: number;
  };
  refreshData: () => Promise<void>;
}

const JournalContext = createContext<JournalContextType | undefined>(undefined);

export const useJournal = () => {
  const context = useContext(JournalContext);
  if (!context) {
    throw new Error('useJournal must be used within a JournalContextProvider');
  }
  return context;
};

export const JournalContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [mindshifts, setMindshifts] = useState<MindShift[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [dailyMoods, setDailyMoods] = useState<DailyMoodEntry[]>([]);
  const [currentMood, setCurrentMood] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const loadJournalEntries = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      const entries: JournalEntry[] = (data || []).map(entry => ({
        id: entry.id,
        date: entry.date,
        content: entry.content,
        mood: entry.mood || undefined,
        tags: entry.tags || [],
        isPinned: entry.is_pinned,
        isStarred: entry.is_starred,
        isArchived: entry.is_archived,
        updatedAt: entry.updated_at
      }));

      setJournalEntries(entries);
    } catch (error) {
      console.error('Error loading journal entries:', error);
    }
  };

  const loadMindshifts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('mindshifts')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      const shifts: MindShift[] = (data || []).map(shift => ({
        id: shift.id,
        date: shift.date,
        content: shift.content,
        category: shift.category || undefined,
        tags: shift.tags || [],
        isPinned: shift.is_pinned,
        isStarred: shift.is_starred,
        relatedJournalId: shift.related_journal_id || undefined
      }));

      setMindshifts(shifts);
    } catch (error) {
      console.error('Error loading mindshifts:', error);
    }
  };

  const loadActions = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      const actionsList: Action[] = (data || []).map(action => ({
        id: action.id,
        content: action.content,
        description: action.description || undefined,
        date: action.date,
        dueDate: action.due_date || undefined,
        completed: action.completed,
        completedDate: action.completed_date || undefined,
        isPinned: action.is_pinned,
        isRecurring: action.is_recurring,
        recurringFrequency: action.recurring_frequency || undefined,
        tags: action.tags || [],
        fromMindshiftId: action.from_mindshift_id || undefined,
        relatedEntryId: action.related_entry_id || undefined,
        reflection: action.reflection || undefined
      }));

      setActions(actionsList);
    } catch (error) {
      console.error('Error loading actions:', error);
    }
  };

  const loadNotes = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const notesList: Note[] = (data || []).map(note => ({
        id: note.id,
        content: note.content,
        pinned: note.pinned,
        createdAt: note.created_at,
        updatedAt: note.updated_at
      }));

      setNotes(notesList);
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const loadDailyMoods = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('daily_moods')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      const moodsList: DailyMoodEntry[] = (data || []).map(mood => ({
        id: mood.id,
        date: mood.date,
        mood: mood.mood as 'positive' | 'neutral' | 'challenging',
        energy: mood.energy,
        stress: mood.stress,
        gratitude: mood.gratitude,
        clarity: mood.clarity,
        connection: mood.connection,
        notes: mood.notes || undefined,
        createdAt: mood.created_at
      }));

      setDailyMoods(moodsList);
    } catch (error) {
      console.error('Error loading daily moods:', error);
    }
  };

  const refreshData = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await Promise.all([
        loadJournalEntries(),
        loadMindshifts(),
        loadActions(),
        loadNotes(),
        loadDailyMoods()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      refreshData();
    } else {
      setJournalEntries([]);
      setMindshifts([]);
      setActions([]);
      setNotes([]);
      setDailyMoods([]);
    }
  }, [isLoggedIn, user?.id]);

  const addJournalEntry = async (content: string, mood?: string, tags?: string[]) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('journal_entries')
      .insert({
        user_id: user.id,
        date: format(new Date(), 'yyyy-MM-dd'),
        content,
        mood: mood || null,
        tags: tags || [],
        is_pinned: false,
        is_starred: false,
        is_archived: false
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding journal entry:', error);
      return;
    }

    if (data) {
      const newEntry: JournalEntry = {
        id: data.id,
        date: data.date,
        content: data.content,
        mood: data.mood || undefined,
        tags: data.tags || [],
        isPinned: data.is_pinned,
        isStarred: data.is_starred,
        isArchived: data.is_archived,
        updatedAt: data.updated_at
      };
      setJournalEntries([newEntry, ...journalEntries]);
    }
  };

  const updateJournalEntry = async (id: string, updates: Partial<JournalEntry>) => {
    if (!user) return;

    const dbUpdates: any = {};
    if (updates.content !== undefined) dbUpdates.content = updates.content;
    if (updates.mood !== undefined) dbUpdates.mood = updates.mood;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
    if (updates.isStarred !== undefined) dbUpdates.is_starred = updates.isStarred;
    if (updates.isArchived !== undefined) dbUpdates.is_archived = updates.isArchived;

    const { error } = await supabase
      .from('journal_entries')
      .update(dbUpdates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating journal entry:', error);
      return;
    }

    setJournalEntries(journalEntries.map(entry =>
      entry.id === id ? { ...entry, ...updates } : entry
    ));
  };

  const deleteJournalEntry = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting journal entry:', error);
      return;
    }

    setJournalEntries(journalEntries.filter(entry => entry.id !== id));
  };

  const toggleEntryPinned = async (id: string) => {
    const entry = journalEntries.find(e => e.id === id);
    if (entry) {
      await updateJournalEntry(id, { isPinned: !entry.isPinned });
    }
  };

  const toggleEntryStarred = async (id: string) => {
    const entry = journalEntries.find(e => e.id === id);
    if (entry) {
      await updateJournalEntry(id, { isStarred: !entry.isStarred });
    }
  };

  const toggleEntryArchived = async (id: string) => {
    const entry = journalEntries.find(e => e.id === id);
    if (entry) {
      await updateJournalEntry(id, { isArchived: !entry.isArchived });
    }
  };

  const searchJournalEntries = (query: string): JournalEntry[] => {
    const searchTerm = query.toLowerCase();
    return journalEntries.filter((entry) => {
      const contentMatch = entry.content.toLowerCase().includes(searchTerm);
      const tagMatch = entry.tags?.some((tag) => tag.toLowerCase().includes(searchTerm));
      const moodMatch = entry.mood?.toLowerCase().includes(searchTerm);
      return contentMatch || tagMatch || moodMatch;
    });
  };

  const filterJournalEntries = (options: {
    tags?: string[];
    mood?: string;
    startDate?: string;
    endDate?: string;
    archived?: boolean;
  }): JournalEntry[] => {
    return journalEntries.filter((entry) => {
      if (options.archived !== undefined && entry.isArchived !== options.archived) return false;
      if (options.tags?.length && !options.tags.some((tag) => entry.tags?.includes(tag))) return false;
      if (options.mood && entry.mood !== options.mood) return false;
      if (options.startDate && entry.date < options.startDate) return false;
      if (options.endDate && entry.date > options.endDate) return false;
      return true;
    });
  };

  const addMindshift = async (content: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('mindshifts')
      .insert({
        user_id: user.id,
        date: format(new Date(), 'yyyy-MM-dd'),
        content,
        is_pinned: false,
        is_starred: false,
        tags: []
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding mindshift:', error);
      return;
    }

    if (data) {
      const newShift: MindShift = {
        id: data.id,
        date: data.date,
        content: data.content,
        category: data.category || undefined,
        tags: data.tags || [],
        isPinned: data.is_pinned,
        isStarred: data.is_starred,
        relatedJournalId: data.related_journal_id || undefined
      };
      setMindshifts([newShift, ...mindshifts]);
    }
  };

  const updateMindshift = async (id: string, updates: Partial<MindShift>) => {
    if (!user) return;

    const dbUpdates: any = {};
    if (updates.content !== undefined) dbUpdates.content = updates.content;
    if (updates.category !== undefined) dbUpdates.category = updates.category;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
    if (updates.isStarred !== undefined) dbUpdates.is_starred = updates.isStarred;

    const { error } = await supabase
      .from('mindshifts')
      .update(dbUpdates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating mindshift:', error);
      return;
    }

    setMindshifts(mindshifts.map(shift =>
      shift.id === id ? { ...shift, ...updates } : shift
    ));
  };

  const deleteMindshift = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('mindshifts')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting mindshift:', error);
      return;
    }

    setMindshifts(mindshifts.filter(shift => shift.id !== id));
  };

  const toggleMindshiftPinned = async (id: string) => {
    const shift = mindshifts.find(s => s.id === id);
    if (shift) {
      await updateMindshift(id, { isPinned: !shift.isPinned });
    }
  };

  const toggleMindshiftStarred = async (id: string) => {
    const shift = mindshifts.find(s => s.id === id);
    if (shift) {
      await updateMindshift(id, { isStarred: !shift.isStarred });
    }
  };

  const searchMindshifts = (query: string): MindShift[] => {
    const searchTerm = query.toLowerCase();
    return mindshifts.filter((shift) => {
      const contentMatch = shift.content.toLowerCase().includes(searchTerm);
      const tagMatch = shift.tags?.some((tag) => tag.toLowerCase().includes(searchTerm));
      return contentMatch || tagMatch;
    });
  };

  const filterMindshifts = (options: {
    tags?: string[];
    category?: string;
    startDate?: string;
    endDate?: string;
    onlyStarred?: boolean;
    onlyPinned?: boolean;
  }): MindShift[] => {
    return mindshifts.filter((shift) => {
      if (options.onlyStarred && !shift.isStarred) return false;
      if (options.onlyPinned && !shift.isPinned) return false;
      if (options.category && shift.category !== options.category) return false;
      if (options.tags?.length && !options.tags.some((tag) => shift.tags?.includes(tag))) return false;
      if (options.startDate && shift.date < options.startDate) return false;
      if (options.endDate && shift.date > options.endDate) return false;
      return true;
    });
  };

  const addAction = async (
    content: string,
    description?: string,
    options?: {
      dueDate?: string;
      isRecurring?: boolean;
      recurringFrequency?: 'daily' | 'weekly' | 'monthly';
      tags?: string[];
      fromMindshiftId?: string;
      relatedEntryId?: string;
    }
  ) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('actions')
      .insert({
        user_id: user.id,
        content,
        description: description || null,
        date: format(new Date(), 'yyyy-MM-dd'),
        due_date: options?.dueDate || null,
        completed: false,
        is_pinned: false,
        is_recurring: options?.isRecurring || false,
        recurring_frequency: options?.recurringFrequency || null,
        tags: options?.tags || [],
        from_mindshift_id: options?.fromMindshiftId || null,
        related_entry_id: options?.relatedEntryId || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding action:', error);
      return;
    }

    if (data) {
      const newAction: Action = {
        id: data.id,
        content: data.content,
        description: data.description || undefined,
        date: data.date,
        dueDate: data.due_date || undefined,
        completed: data.completed,
        completedDate: data.completed_date || undefined,
        isPinned: data.is_pinned,
        isRecurring: data.is_recurring,
        recurringFrequency: data.recurring_frequency || undefined,
        tags: data.tags || [],
        fromMindshiftId: data.from_mindshift_id || undefined,
        relatedEntryId: data.related_entry_id || undefined,
        reflection: data.reflection || undefined
      };
      setActions([newAction, ...actions]);
    }
  };

  const generateActionSuggestion = async (content: string) => {
    try {
      const result = await callQuickInsight('action', content);
      if (result.actions && result.actions.length > 0) {
        return result.actions.map((a: any) => `${a.action}: ${a.why}`).join('\n');
      }
      return result.encouragement || '';
    } catch (error) {
      console.error('Error generating action suggestion:', error);
      return '';
    }
  };

  const addActionFromMindshift = async (mindshiftId: string, title: string) => {
    const mindshift = mindshifts.find(m => m.id === mindshiftId);
    if (mindshift) {
      await addAction(title, undefined, {
        fromMindshiftId: mindshiftId,
        tags: mindshift.tags,
      });
    }
  };

  const updateActionReflection = async (id: string, reflection: string) => {
    await updateAction(id, { reflection });
  };

  const getActionSuccessRate = () => {
    const completed = actions.filter(a => a.completed).length;
    return actions.length > 0 ? (completed / actions.length) * 100 : 0;
  };

  const getTodaysActions = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return actions.filter(action => {
      if (action.completed) return false;
      if (action.dueDate === today) return true;
      if (action.isRecurring) {
        const lastCompleted = action.completedDate
          ? new Date(action.completedDate)
          : new Date(0);
        const daysSinceCompleted = differenceInDays(new Date(), lastCompleted);

        switch (action.recurringFrequency) {
          case 'daily': return daysSinceCompleted >= 1;
          case 'weekly': return daysSinceCompleted >= 7;
          case 'monthly': return daysSinceCompleted >= 30;
          default: return false;
        }
      }
      return false;
    });
  };

  const getPinnedActions = () => {
    return actions.filter(action => action.isPinned && !action.completed);
  };

  const getCompletedActions = () => {
    return actions
      .filter(action => action.completed)
      .sort((a, b) => new Date(b.completedDate!).getTime() - new Date(a.completedDate!).getTime());
  };

  const toggleActionCompleted = async (id: string) => {
    const action = actions.find(a => a.id === id);
    if (!action || !user) return;

    const newCompleted = !action.completed;
    const completedDate = newCompleted ? format(new Date(), 'yyyy-MM-dd') : null;

    const { error } = await supabase
      .from('actions')
      .update({
        completed: newCompleted,
        completed_date: completedDate
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error toggling action completed:', error);
      return;
    }

    setActions(actions.map(a =>
      a.id === id ? { ...a, completed: newCompleted, completedDate: completedDate || undefined } : a
    ));
  };

  const toggleActionPinned = async (id: string) => {
    const action = actions.find(a => a.id === id);
    if (!action || !user) return;

    const { error } = await supabase
      .from('actions')
      .update({ is_pinned: !action.isPinned })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error toggling action pinned:', error);
      return;
    }

    setActions(actions.map(a =>
      a.id === id ? { ...a, isPinned: !a.isPinned } : a
    ));
  };

  const deleteAction = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('actions')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting action:', error);
      return;
    }

    setActions(actions.filter(a => a.id !== id));
  };

  const updateAction = async (id: string, updates: Partial<Action>) => {
    if (!user) return;

    const dbUpdates: any = {};
    if (updates.content !== undefined) dbUpdates.content = updates.content;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
    if (updates.completed !== undefined) dbUpdates.completed = updates.completed;
    if (updates.completedDate !== undefined) dbUpdates.completed_date = updates.completedDate;
    if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
    if (updates.isRecurring !== undefined) dbUpdates.is_recurring = updates.isRecurring;
    if (updates.recurringFrequency !== undefined) dbUpdates.recurring_frequency = updates.recurringFrequency;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.reflection !== undefined) dbUpdates.reflection = updates.reflection;

    const { error } = await supabase
      .from('actions')
      .update(dbUpdates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating action:', error);
      return;
    }

    setActions(actions.map(a =>
      a.id === id ? { ...a, ...updates } : a
    ));
  };

  const addNote = async (content: string) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('notes')
      .insert({
        user_id: user.id,
        content,
        pinned: false
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding note:', error);
      return;
    }

    if (data) {
      const newNote: Note = {
        id: data.id,
        content: data.content,
        pinned: data.pinned,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
      setNotes([newNote, ...notes]);
    }
  };

  const updateNote = async (id: string, content: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('notes')
      .update({ content })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating note:', error);
      return;
    }

    setNotes(notes.map(n =>
      n.id === id ? { ...n, content, updatedAt: new Date().toISOString() } : n
    ));
  };

  const toggleNotePinned = async (id: string) => {
    const note = notes.find(n => n.id === id);
    if (!note || !user) return;

    const { error } = await supabase
      .from('notes')
      .update({ pinned: !note.pinned })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error toggling note pinned:', error);
      return;
    }

    setNotes(notes.map(n =>
      n.id === id ? { ...n, pinned: !n.pinned } : n
    ));
  };

  const deleteNote = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting note:', error);
      return;
    }

    setNotes(notes.filter(n => n.id !== id));
  };

  const getLoginStreak = () => {
    return 1;
  };

  const getWeeklyEntries = () => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return journalEntries.filter((entry) => {
      const entryDate = new Date(entry.date);
      return entryDate >= oneWeekAgo && entryDate <= today;
    });
  };

  const getWeeklyMindshifts = () => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return mindshifts.filter((shift) => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= oneWeekAgo && shiftDate <= today;
    });
  };

  const getCurrentMood = () => currentMood;

  const addDailyMood = async (moodData: Omit<DailyMoodEntry, 'id' | 'date' | 'createdAt'>) => {
    if (!user) return;

    const today = format(new Date(), 'yyyy-MM-dd');

    const { data, error } = await supabase
      .from('daily_moods')
      .insert({
        user_id: user.id,
        date: today,
        mood: moodData.mood,
        energy: moodData.energy,
        stress: moodData.stress,
        gratitude: moodData.gratitude,
        clarity: moodData.clarity,
        connection: moodData.connection,
        notes: moodData.notes || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding daily mood:', error);
      return;
    }

    if (data) {
      const newMood: DailyMoodEntry = {
        id: data.id,
        date: data.date,
        mood: data.mood as 'positive' | 'neutral' | 'challenging',
        energy: data.energy,
        stress: data.stress,
        gratitude: data.gratitude,
        clarity: data.clarity,
        connection: data.connection,
        notes: data.notes || undefined,
        createdAt: data.created_at
      };
      setDailyMoods([newMood, ...dailyMoods]);
    }
  };

  const getTodaysMoodEntry = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return dailyMoods.find(mood => mood.date === today);
  };

  const hasMoodEntryForToday = () => {
    return getTodaysMoodEntry() !== undefined;
  };

  const getWeeklyMoodData = () => {
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    return dailyMoods.filter((mood) => {
      const moodDate = new Date(mood.date);
      return moodDate >= oneWeekAgo && moodDate <= today;
    });
  };

  const getMoodTrends = () => {
    const recentMoods = getWeeklyMoodData();

    if (recentMoods.length === 0) {
      return {
        averageMood: 0,
        averageEnergy: 0,
        averageStress: 0,
        averageGratitude: 0,
        averageClarity: 0,
        averageConnection: 0,
      };
    }

    const moodValues = recentMoods.map(m => {
      switch (m.mood) {
        case 'positive': return 3;
        case 'neutral': return 2;
        case 'challenging': return 1;
        default: return 2;
      }
    });

    return {
      averageMood: moodValues.reduce((sum, val) => sum + val, 0) / moodValues.length,
      averageEnergy: recentMoods.reduce((sum, m) => sum + m.energy, 0) / recentMoods.length,
      averageStress: recentMoods.reduce((sum, m) => sum + m.stress, 0) / recentMoods.length,
      averageGratitude: recentMoods.reduce((sum, m) => sum + m.gratitude, 0) / recentMoods.length,
      averageClarity: recentMoods.reduce((sum, m) => sum + m.clarity, 0) / recentMoods.length,
      averageConnection: recentMoods.reduce((sum, m) => sum + m.connection, 0) / recentMoods.length,
    };
  };

  const value = {
    journalEntries,
    isLoading,
    generateActionSuggestion,
    addActionFromMindshift,
    updateActionReflection,
    getActionSuccessRate,
    getTodaysActions,
    getPinnedActions,
    getCompletedActions,
    addJournalEntry,
    updateJournalEntry,
    deleteJournalEntry,
    toggleEntryPinned,
    toggleEntryStarred,
    toggleEntryArchived,
    searchJournalEntries,
    filterJournalEntries,
    mindshifts,
    addMindshift,
    updateMindshift,
    deleteMindshift,
    toggleMindshiftPinned,
    toggleMindshiftStarred,
    searchMindshifts,
    filterMindshifts,
    actions,
    addAction,
    toggleActionCompleted,
    toggleActionPinned,
    deleteAction,
    updateAction,
    notes,
    addNote,
    updateNote,
    toggleNotePinned,
    deleteNote,
    getLoginStreak,
    getWeeklyEntries,
    getWeeklyMindshifts,
    getCurrentMood,
    setCurrentMood,
    dailyMoods,
    addDailyMood,
    getTodaysMoodEntry,
    hasMoodEntryForToday,
    getWeeklyMoodData,
    getMoodTrends,
    refreshData
  };

  return <JournalContext.Provider value={value}>{children}</JournalContext.Provider>;
};
